import { TrazoDeTinta, tocaTrazo } from "@gestiondoc/plantilla";
import {
  type ColorDeTinta,
  MAX_PUNTOS_POR_TRAZO,
  nuevoIdDeBloque,
  type Tinta,
  type Trazo,
} from "@gestiondoc/shared";
import { memo, useEffect, useMemo, useRef, useState } from "react";

/**
 * La capa de tinta de una hoja llena: un SVG del tamaño de la hoja, encima de la `HojaISO`,
 * donde se escribe con el lápiz como en GoodNotes.
 *
 * Cómo reparte los punteros, pensado para la tablet:
 * - el lápiz escribe (y con el botón de goma del lápiz, borra);
 * - el dedo es del navegador: desplaza y hace zoom con sus gestos nativos, con su inercia. Solo
 *   escribe si se activa "Escribir con el dedo" (teléfono o tablet sin lápiz);
 * - mientras el lápiz está cerca, los toques no desplazan: es la palma apoyada;
 * - el ratón escribe, para el escritorio.
 *
 * El truco es no poner `touch-action: none` fijo (eso le quita al dedo el desplazamiento y el
 * zoom del sistema). Se deja `pan-x pan-y pinch-zoom`, y al lápiz se le impide desplazar de dos
 * maneras: en Safari, cancelando el `touchstart` del Apple Pencil (`touchType === "stylus"`); en
 * Chromium (Surface, Android), cambiando a `none` en cuanto el lápiz se acerca, porque esos
 * lápices avisan antes de tocar la pantalla (eventos de hover) y el navegador decide el
 * `touch-action` al momento del contacto.
 */

export type HerramientaActiva = "lapiz" | "resaltador" | "borrador";

export type Pluma = {
  herramienta: HerramientaActiva;
  color: ColorDeTinta;
  grosor: number;
};

/** Radio de la goma en píxeles de la hoja. */
const RADIO_GOMA = 9;
/** Tiempo que el lápiz "sigue cerca" después de su último evento, para ignorar la palma. */
const PALMA_MS = 600;
const GESTOS_DEL_DEDO = "pan-x pan-y pinch-zoom";

/** ¿El lápiz dio señales hace menos de PALMA_MS? */
function cerca(ultimoLapiz: number): boolean {
  return performance.now() - ultimoLapiz < PALMA_MS;
}

type Gesto =
  | { tipo: "trazo"; pointerId: number; trazo: Trazo; lapiz: boolean }
  | { tipo: "goma"; pointerId: number; lapiz: boolean };

export function CapaDeTinta({
  tinta,
  onCambio,
  pluma,
  editable,
  conDedo,
  onLapiz,
  fijos = SIN_FIJOS,
}: {
  tinta: Tinta;
  onCambio: (nueva: Tinta) => void;
  pluma: Pluma;
  editable: boolean;
  /** El dedo escribe en vez de desplazar. */
  conDedo: boolean;
  /** Se vio un lápiz: la pantalla apaga "Escribir con el dedo". */
  onLapiz: () => void;
  /** Trazos de otros días: la goma los salta (lo escrito un día queda fijo). */
  fijos?: ReadonlySet<string>;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const gesto = useRef<Gesto | null>(null);
  const ultimoLapiz = useRef(0);
  const soltarLapiz = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cuadro = useRef(0);
  const [vivo, setVivo] = useState<Trazo | null>(null);
  const [borrados, setBorrados] = useState<ReadonlySet<string>>(new Set());

  // Lo que los manejadores de eventos necesitan leer sin volver a crearse.
  const actual = useRef({ tinta, onCambio, pluma, conDedo, onLapiz, borrados, editable, fijos });
  actual.current = { tinta, onCambio, pluma, conDedo, onLapiz, borrados, editable, fijos };

  /** El touch-action se pone a mano (no por React) porque cambia entre dos eventos seguidos. */
  function aplicarGestos() {
    const s = svg.current;
    if (!s) return;
    const { editable, conDedo } = actual.current;
    s.style.touchAction = !editable
      ? "auto"
      : conDedo || cerca(ultimoLapiz.current) || gesto.current
        ? "none"
        : GESTOS_DEL_DEDO;
  }

  /** El lápiz está cerca: la palma no desplaza hasta un rato después de su último evento. */
  function marcarLapiz() {
    ultimoLapiz.current = performance.now();
    aplicarGestos();
    if (soltarLapiz.current) clearTimeout(soltarLapiz.current);
    soltarLapiz.current = setTimeout(aplicarGestos, PALMA_MS + 20);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: aplicarGestos lee todo de refs
  useEffect(aplicarGestos, [editable, conDedo]);

  // Safari: el Apple Pencil llega también como touch; cancelar su touchstart evita que desplace.
  // Lo mismo con la palma mientras el lápiz está cerca, y con el dedo cuando escribe.
  useEffect(() => {
    const s = svg.current;
    if (!s) return;
    const alTocar = (e: TouchEvent) => {
      if (!actual.current.editable) return;
      const conLapiz = Array.from(e.touches).some(
        (t) => (t as Touch & { touchType?: string }).touchType === "stylus",
      );
      if (
        conLapiz ||
        actual.current.conDedo ||
        cerca(ultimoLapiz.current) ||
        gesto.current?.lapiz
      ) {
        if (e.cancelable) e.preventDefault();
      }
    };
    s.addEventListener("touchstart", alTocar, { passive: false });
    s.addEventListener("touchmove", alTocar, { passive: false });
    return () => {
      s.removeEventListener("touchstart", alTocar);
      s.removeEventListener("touchmove", alTocar);
    };
  }, []);

  useEffect(
    () => () => {
      cancelAnimationFrame(cuadro.current);
      if (soltarLapiz.current) clearTimeout(soltarLapiz.current);
    },
    [],
  );

  function aHoja(e: { clientX: number; clientY: number }): [number, number] {
    // Píxeles de la hoja a tamaño real: el ancho sin escalar de la capa (vertical u horizontal)
    // sobre el ancho con el que se ve.
    const s = svg.current;
    const r = s?.getBoundingClientRect();
    const real = (s?.parentElement as HTMLElement | null)?.offsetWidth ?? 0;
    if (!r?.width || !real) return [0, 0];
    const f = real / r.width;
    return [(e.clientX - r.left) * f, (e.clientY - r.top) * f];
  }

  function redibujar() {
    cancelAnimationFrame(cuadro.current);
    cuadro.current = requestAnimationFrame(() => {
      const g = gesto.current;
      setVivo(g?.tipo === "trazo" ? { ...g.trazo, puntos: [...g.trazo.puntos] } : null);
    });
  }

  function borrarEn(x: number, y: number) {
    const { tinta, borrados, fijos } = actual.current;
    const nuevos = tinta.filter(
      (t) => !borrados.has(t.id) && !fijos.has(t.id) && tocaTrazo(t, x, y, RADIO_GOMA),
    );
    if (nuevos.length) setBorrados((b) => new Set([...b, ...nuevos.map((t) => t.id)]));
  }

  function alBajar(e: React.PointerEvent<SVGSVGElement>) {
    if (!editable) return;
    const { pluma, conDedo, onLapiz } = actual.current;
    const esLapiz = e.pointerType === "pen";
    if (esLapiz) {
      marcarLapiz();
      onLapiz();
      const g = gesto.current;
      if (g && !g.lapiz) {
        // El lápiz manda: si la palma (o el dedo) había empezado algo, se descarta.
        gesto.current = null;
        setVivo(null);
        setBorrados(new Set());
      } else if (g) {
        // El lápiz solo puede estar en un sitio: si baja con un gesto suyo aún abierto es que
        // Safari se tragó el pointerup del anterior (pasa con toques rápidos, como al escribir
        // pequeño). Se cierra con lo que alcanzó a dibujarse y empieza el nuevo; si no, se
        // ignorarían todos los trazos siguientes.
        terminar(g, false);
      }
    }
    if (gesto.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // El dedo es del navegador (desplazar, zoom), salvo que escriba; la palma, de nadie.
    if (e.pointerType === "touch" && (!conDedo || cerca(ultimoLapiz.current))) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    // El botón de goma del lápiz (o darle la vuelta, en los que tienen goma atrás) borra.
    const goma =
      pluma.herramienta === "borrador" || (esLapiz && (e.button === 5 || (e.buttons & 32) !== 0));
    const [x, y] = aHoja(e);
    if (goma) {
      gesto.current = { tipo: "goma", pointerId: e.pointerId, lapiz: esLapiz };
      aplicarGestos();
      borrarEn(x, y);
      return;
    }
    const resaltador = pluma.herramienta === "resaltador";
    gesto.current = {
      tipo: "trazo",
      pointerId: e.pointerId,
      lapiz: esLapiz,
      trazo: {
        id: nuevoIdDeBloque(),
        herramienta: resaltador ? "resaltador" : "lapiz",
        color: resaltador ? "amarillo" : pluma.color,
        grosor: pluma.grosor,
        presion: esLapiz,
        puntos: [redondear(x), redondear(y), presionDe(e)],
      },
    };
    aplicarGestos();
    redibujar();
  }

  function alMover(e: React.PointerEvent<SVGSVGElement>) {
    // También el lápiz que flota sin tocar: así el touch-action ya es "none" cuando toca.
    if (e.pointerType === "pen" && editable) marcarLapiz();
    const g = gesto.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const eventos = e.nativeEvent.getCoalescedEvents?.() ?? [];
    const muestras = eventos.length ? eventos : [e.nativeEvent];
    if (g.tipo === "goma") {
      for (const m of muestras) borrarEn(...aHoja(m));
      return;
    }
    const p = g.trazo.puntos;
    for (const m of muestras) {
      if (p.length >= MAX_PUNTOS_POR_TRAZO * 3) break;
      const [x, y] = aHoja(m);
      // Puntos casi encima del anterior no aportan nada y engordan lo que se guarda.
      if (Math.hypot(x - (p[p.length - 3] ?? 0), y - (p[p.length - 2] ?? 0)) < 0.6) continue;
      p.push(redondear(x), redondear(y), presionDe(m));
    }
    redibujar();
  }

  function alSoltar(e: React.PointerEvent<SVGSVGElement>) {
    const g = gesto.current;
    if (!g || g.pointerId !== e.pointerId) return;
    terminar(g, e.type === "pointercancel");
  }

  /** Cierra el gesto: la goma aplica lo borrado y el trazo se guarda con lo que tenga. */
  function terminar(g: Gesto, cancelado: boolean) {
    gesto.current = null;
    aplicarGestos();
    const { tinta, onCambio, borrados } = actual.current;
    if (g.tipo === "goma") {
      if (borrados.size) onCambio(tinta.filter((t) => !borrados.has(t.id)));
      setBorrados(new Set());
      return;
    }
    cancelAnimationFrame(cuadro.current);
    setVivo(null);
    // Si el sistema se llevó el puntero a mitad de trazo, lo que alcanzó a dibujarse se queda.
    // Del lápiz se queda hasta un punto solo: al escribir pequeño, los puntos de las íes, las
    // tildes y los acentos son trazos de nada, y son justo los que el sistema cancela. Del dedo
    // no: un toque cancelado suele ser el inicio de un desplazamiento.
    if (cancelado && !g.lapiz && g.trazo.puntos.length < 6) return;
    onCambio([...tinta, g.trazo]);
  }

  const visibles = useMemo(
    () => (borrados.size ? tinta.filter((t) => !borrados.has(t.id)) : tinta),
    [tinta, borrados],
  );
  const resaltados = visibles.filter((t) => t.herramienta === "resaltador");
  const escritos = visibles.filter((t) => t.herramienta !== "resaltador");

  return (
    <svg
      ref={svg}
      className="capa-tinta"
      data-herramienta={editable ? pluma.herramienta : undefined}
      style={{ pointerEvents: editable ? "auto" : "none" }}
      onPointerDown={alBajar}
      onPointerMove={alMover}
      onPointerUp={alSoltar}
      onPointerCancel={alSoltar}
      // Si el navegador suelta la captura sin pointerup ni pointercancel, el trazo no se pierde.
      onLostPointerCapture={alSoltar}
      onContextMenu={(e) => e.preventDefault()}
      aria-hidden="true"
    >
      <g className="capa-tinta-resaltador">
        {resaltados.map((t) => (
          <TrazoSvg key={t.id} trazo={t} />
        ))}
        {vivo?.herramienta === "resaltador" && <TrazoSvg trazo={vivo} enCurso />}
      </g>
      {escritos.map((t) => (
        <TrazoSvg key={t.id} trazo={t} />
      ))}
      {vivo && vivo.herramienta !== "resaltador" && <TrazoSvg trazo={vivo} enCurso />}
    </svg>
  );
}

const SIN_FIJOS: ReadonlySet<string> = new Set();

/** Un trazo ya hecho no cambia: se memoriza por referencia y su contorno se calcula una vez. */
const TrazoSvg = memo(TrazoDeTinta);

function redondear(v: number): number {
  return Math.round(v * 10) / 10;
}

function presionDe(e: { pointerType: string; pressure: number }): number {
  // El ratón y el dedo reportan 0.5 (o 0 / 1): no es presión real; el trazo la simula.
  if (e.pointerType !== "pen") return 0.5;
  return Math.round((e.pressure || 0.5) * 100) / 100;
}
